import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  ChevronDown, ChevronRight, Plus, Trash2, Pencil,
  Eye, EyeOff, GripVertical, RefreshCw, ListChecks
} from 'lucide-react';
import { ReceiptViewerPanel } from '@/components/furniture/ReceiptViewerPanel';
import { EssentialPickerDialog } from '@/components/furniture/EssentialPickerDialog';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { fmtTotal } from '@/components/furniture/tables/utils';
import { ItemRow } from '@/components/furniture/tables/ItemRow';
import { FillInWizard } from '@/components/furniture/FillInWizard';

export const RoomDetail = ({
  room, expanded, onToggle, onUpdateRoom, onDeleteRoom,
  onAddItem, onUpdateItem, onDeleteItem, onReorderItems,
  onSyncDefaultLinks, readOnly,
  projectId, shareToken, viewToken,
  receiptsByItem = {}, onUploadReceipt,
  dragHandleProps, dragAttributes, sortableRef, sortableStyle, isDragging
}) => {
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(room.name);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadingReceiptId, setUploadingReceiptId] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [viewingItemId, setViewingItemId] = useState(null);
  const [confirmDeleteRoom, setConfirmDeleteRoom] = useState(false);
  // 'items' | 'essentials' | null — open the wizard scoped to that section.
  const [fillInKind, setFillInKind] = useState(null);
  // Room-level toggle for the Links column. When true, hidden links are
  // rendered inline (greyed/struck-through) so the user can see them and
  // either click "Unhide" to make a hidden link permanent again or just
  // toggle this back off to hide them all again.
  const [showHiddenLinks, setShowHiddenLinks] = useState(false);

  const allItems = room.items || [];
  const hasHiddenLinks = allItems.some((i) => (i.hidden_link_urls || []).length > 0);

  const hideLink = (item) => (url) => {
    const cur = item.hidden_link_urls || [];
    if (cur.includes(url)) return;
    onUpdateItem(item.id, { hidden_link_urls: [...cur, url] });
  };
  const unhideLink = (item) => (url) => {
    const cur = item.hidden_link_urls || [];
    onUpdateItem(item.id, { hidden_link_urls: cur.filter((u) => u !== url) });
  };

  // "Choose this link" — marks one link as the kept one and auto-hides the
  // rest in the same item. Surfaces a 6s Sonner toast with an Undo button
  // that restores the previous hidden_link_urls (matches the rest of the
  // app's Undo pattern for destructive-ish actions).
  const chooseLink = (item) => (url) => {
    const links = Array.isArray(item.links) ? item.links.filter(Boolean) : (item.link ? [item.link] : []);
    if (links.length <= 1) return; // nothing to auto-hide
    const prevHidden = [...(item.hidden_link_urls || [])];
    const newHidden = links.filter((u) => u !== url);
    // No-op if the user re-chooses the already-chosen link.
    const same = prevHidden.length === newHidden.length
      && prevHidden.every((u) => newHidden.includes(u));
    if (same) return;
    onUpdateItem(item.id, { hidden_link_urls: newHidden });
    toast.success('Link chosen — others hidden', {
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          onUpdateItem(item.id, { hidden_link_urls: prevHidden });
        },
      },
    });
  };

  const regularItems = allItems.filter(i => !i.is_essential);
  const essentialItems = allItems.filter(i => i.is_essential);

  // Calculations
  const calcTotal = (items, field) => items.reduce((s, i) => s + (Number(i[field]) || 0), 0);
  const totalBudget = calcTotal(allItems, 'budget');
  const totalListed = calcTotal(allItems, 'listed_price');
  const totalOrdered = calcTotal(allItems, 'ordered_price');
  const budgetStatus = totalBudget - totalOrdered;

  const saveName = () => {
    setEditingName(false);
    if (nameVal.trim() && nameVal !== room.name) {
      onUpdateRoom({ name: nameVal.trim() });
    }
  };

  const openMoodBoard = () => {
    if (shareToken) {
      navigate(`/furniture-projects/share/${shareToken}/rooms/${room.id}/mood-board`);
    } else if (viewToken) {
      navigate(`/furniture-projects/view/${viewToken}/rooms/${room.id}/mood-board`);
    } else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
      // Designers browse under /staff/*; the outer App only mounts the staff
      // router tree while the pathname stays in that namespace, so we must
      // keep the mood-board URL prefixed with /staff/ — otherwise the staff
      // router's `*` fallback bounces them to /staff/home.
      navigate(`/staff/projects/${projectId}/rooms/${room.id}/mood-board`);
    } else {
      navigate(`/furniture-projects/${projectId}/rooms/${room.id}/mood-board`);
    }
  };

  const handleReceiptUpload = async (itemId, file) => {
    if (!onUploadReceipt) return;
    setUploadingReceiptId(itemId);
    try {
      await onUploadReceipt({ file, itemIds: [itemId] });
    } finally {
      setUploadingReceiptId(null);
    }
  };

  // Drag-end handler for items within a group (regular or essential).
  // `group` is 'regular' or 'essential'; we merge the reordered group back
  // into the full items array preserving the other group's order.
  const handleItemDragEnd = (group) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const groupItems = group === 'essential' ? essentialItems : regularItems;
    const oldIdx = groupItems.findIndex(i => i.id === active.id);
    const newIdx = groupItems.findIndex(i => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reorderedGroup = arrayMove(groupItems, oldIdx, newIdx);
    // Rebuild full items list: reordered group + other group (preserving original order)
    const otherGroup = group === 'essential' ? regularItems : essentialItems;
    // Preserve original split order: regulars first then essentials (matches how they're rendered)
    const newItems = group === 'essential'
      ? [...otherGroup, ...reorderedGroup]
      : [...reorderedGroup, ...otherGroup];
    onReorderItems?.(room.id, newItems.map(i => i.id));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const showReceipts = !readOnly && !!onUploadReceipt;

  const renderItemRow = (item) => (
    <ItemRow
      key={item.id}
      item={item}
      readOnly={readOnly}
      showHiddenLinks={showHiddenLinks}
      onUpdateItem={onUpdateItem}
      onDeleteItem={onDeleteItem}
      onHideLink={hideLink(item)}
      onUnhideLink={unhideLink(item)}
      onChooseLink={chooseLink(item)}
      showReceipts={showReceipts}
      uploadingReceiptId={uploadingReceiptId}
      receipts={receiptsByItem[item.id] || []}
      onUploadReceipt={handleReceiptUpload}
      onViewReceipt={(r, itemId) => { setViewingReceipt(r); setViewingItemId(itemId); }}
    />
  );

  return (
    <Card
      ref={sortableRef}
      style={sortableStyle}
      className={`shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_30px_rgba(16,24,40,0.06)] overflow-hidden ${isDragging ? 'ring-2 ring-[#2d948c]/40 shadow-xl' : ''}`}
      data-testid={`room-card-${room.id}`}
    >
      {/* Room Header */}
      <div
        className="flex items-center gap-3 px-4 sm:px-5 py-3 cursor-pointer hover:bg-[#2d948c]/5 transition-colors border-b"
        onClick={onToggle}
        data-testid={`room-toggle-${room.id}`}
      >
        {!readOnly && dragHandleProps && (
          <button
            {...dragHandleProps}
            {...(dragAttributes || {})}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab active:cursor-grabbing text-[#5b6773]/40 hover:text-[#2d948c] -ml-1 p-1 touch-none"
            aria-label="Drag to reorder room"
            data-testid={`drag-handle-room-${room.id}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        {expanded ? <ChevronDown className="h-4 w-4 text-[#5b6773]" /> : <ChevronRight className="h-4 w-4 text-[#5b6773]" />}
        <div className="flex-1 min-w-0">
          {editingName && !readOnly ? (
            <Input
              value={nameVal} onChange={e => setNameVal(e.target.value)}
              onBlur={saveName} onKeyDown={e => { if (e.key === 'Enter') saveName(); }}
              onClick={e => e.stopPropagation()} autoFocus
              className="h-7 text-sm font-heading font-semibold" data-testid="edit-room-name"
            />
          ) : (
            <h3 className="font-heading font-semibold text-[#1f2933] truncate">{room.name}</h3>
          )}
        </div>
        {/* Quick summary stats */}
        <div className="hidden sm:flex items-center gap-4 text-xs tabular-nums text-[#5b6773]">
          <span>Budget: <b className="text-[#1f2933]">{fmtTotal(totalBudget)}</b></span>
          <span>Ordered: <b className="text-[#1f2933]">{fmtTotal(totalOrdered)}</b></span>
          <span className={budgetStatus < 0 ? 'text-destructive' : 'text-[#2f7d5a]'}>
            {budgetStatus >= 0 ? '+' : ''}{fmtTotal(budgetStatus)}
          </span>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              variant="outline" size="sm"
              className="h-7 text-xs gap-1 text-[#2d948c] border-[#2d948c]/30 hover:bg-[#2d948c]/10"
              onClick={openMoodBoard}
              data-testid={`view-room-moodboard-${room.id}`}
            >
              <Eye className="h-3 w-3" /> View Room
            </Button>
            {onSyncDefaultLinks && (
              <Button
                variant="ghost" size="icon" className="h-7 w-7 text-[#2f6f8f]"
                onClick={onSyncDefaultLinks}
                title="Re-sync default links for essentials in this room"
                data-testid={`sync-default-links-${room.id}`}
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setNameVal(room.name); setEditingName(true); }} data-testid={`rename-room-${room.id}`}>
              <Pencil className="h-3 w-3" />
            </Button>
            {confirmDeleteRoom ? (
              <div className="flex items-center gap-1" data-testid={`confirm-delete-room-${room.id}`}>
                <Button
                  size="sm" variant="destructive" className="h-7 text-[11px] px-2"
                  onClick={() => { setConfirmDeleteRoom(false); onDeleteRoom(); }}
                  data-testid={`confirm-delete-room-yes-${room.id}`}
                >
                  Delete
                </Button>
                <Button
                  variant="ghost" size="sm" className="h-7 text-[11px] px-2"
                  onClick={() => setConfirmDeleteRoom(false)}
                  data-testid={`confirm-delete-room-no-${room.id}`}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                onClick={() => setConfirmDeleteRoom(true)}
                title="Delete room"
                data-testid={`delete-room-${room.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
        {readOnly && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              variant="outline" size="sm"
              className="h-7 text-xs gap-1 text-[#2d948c] border-[#2d948c]/30 hover:bg-[#2d948c]/10"
              onClick={openMoodBoard}
              data-testid={`view-room-moodboard-${room.id}`}
            >
              <Eye className="h-3 w-3" /> View Room
            </Button>
          </div>
        )}
      </div>

      {expanded && (
        <CardContent className="p-0">
          {/* Room Summary Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 sm:px-5 py-3 bg-[#f3efe6]/40 border-b">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Designer Total</p>
              <p className="text-sm font-heading font-semibold tabular-nums text-[#1f2933]" data-testid={`room-designer-total-${room.id}`}>{fmtTotal(totalListed)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Budget</p>
              <p className="text-sm font-heading font-semibold tabular-nums text-[#1f2933]" data-testid={`room-budget-total-${room.id}`}>{fmtTotal(totalBudget)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Budget Status</p>
              <p className={`text-sm font-heading font-semibold tabular-nums ${budgetStatus < 0 ? 'text-destructive' : 'text-[#2f7d5a]'}`} data-testid={`room-budget-status-${room.id}`}>
                {budgetStatus >= 0 ? '+' : ''}{fmtTotal(budgetStatus)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Ordered Price</p>
              <p className="text-sm font-heading font-semibold tabular-nums text-[#1f2933]" data-testid={`room-ordered-total-${room.id}`}>{fmtTotal(totalOrdered)}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#2d948c]/[0.07]">
                  {!readOnly && <TableHead className="w-8" />}
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Line Item</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">
                    <div className="flex flex-col items-start gap-1">
                      <span>Links</span>
                      {hasHiddenLinks && !readOnly && (
                        <button
                          type="button"
                          onClick={() => setShowHiddenLinks((v) => !v)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium normal-case bg-[#2d948c]/10 text-[#2d948c] hover:bg-[#2d948c]/20 transition-colors tracking-normal"
                          title={showHiddenLinks ? 'Hide all temporarily revealed links again' : 'Reveal all hidden links'}
                          data-testid={`toggle-hidden-links-${room.id}`}
                        >
                          {showHiddenLinks ? <Eye className="h-2.5 w-2.5" /> : <EyeOff className="h-2.5 w-2.5" />}
                          {showHiddenLinks ? 'Rehide' : 'Unhide'}
                        </button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] w-10 text-center" title="Specifications">Specs</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-right">Budget</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-right">Listed Price</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-right">Ordered Price</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] w-10 text-center" title="Notes">Notes</TableHead>
                  {showReceipts && <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Receipt</TableHead>}
                  {!readOnly && <TableHead className="w-8" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd('regular')}>
                  <SortableContext items={regularItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {regularItems.map(item => renderItemRow(item))}
                  </SortableContext>
                </DndContext>

                {/* Add item button row */}
                {!readOnly && (
                  <TableRow>
                    <TableCell colSpan={11} className="py-1">
                      <div className="flex items-center justify-between gap-2">
                        <Button
                          variant="ghost" size="sm"
                          className="text-xs text-[#2d948c] h-7 gap-1"
                          onClick={() => onAddItem({ name: '', is_essential: false })}
                          data-testid={`add-item-${room.id}`}
                        >
                          <Plus className="h-3 w-3" /> Add Item
                        </Button>
                        {regularItems.length > 0 && (
                          <Button
                            variant="outline" size="sm"
                            className="text-xs h-7 gap-1 border-[#2d948c]/30 text-[#2d948c] hover:bg-[#2d948c]/10"
                            onClick={() => setFillInKind('items')}
                            data-testid={`fill-in-items-${room.id}`}
                            title="Step through items missing budget, prices, status, or a receipt"
                          >
                            <ListChecks className="h-3 w-3" /> Fill In
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Essentials Section */}
                {(essentialItems.length > 0 || !readOnly) && (
                  <>
                    <TableRow className="bg-[#f3efe6]/30">
                      <TableCell colSpan={readOnly ? 8 : 11} className="py-2">
                        <span className="text-xs font-heading font-semibold text-[#5b6773] uppercase tracking-wider">Essentials List</span>
                      </TableCell>
                    </TableRow>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd('essential')}>
                      <SortableContext items={essentialItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                        {essentialItems.map(item => renderItemRow(item))}
                      </SortableContext>
                    </DndContext>
                    {!readOnly && (
                      <TableRow>
                        <TableCell colSpan={11} className="py-1">
                          <div className="flex items-center justify-between gap-2">
                            <Button
                              variant="ghost" size="sm"
                              className="text-xs text-[#2d948c] h-7 gap-1"
                              onClick={() => setPickerOpen(true)}
                              data-testid={`add-essential-${room.id}`}
                            >
                              <Plus className="h-3 w-3" /> Add Essential
                            </Button>
                            {essentialItems.length > 0 && (
                              <Button
                                variant="outline" size="sm"
                                className="text-xs h-7 gap-1 border-[#2d948c]/30 text-[#2d948c] hover:bg-[#2d948c]/10"
                                onClick={() => setFillInKind('essentials')}
                                data-testid={`fill-in-essentials-${room.id}`}
                                title="Step through essentials missing budget, prices, status, or a receipt"
                              >
                                <ListChecks className="h-3 w-3" /> Fill In
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}

                {/* Totals Row */}
                <TableRow className="bg-[#2d948c]/10 border-t-2">
                  <TableCell colSpan={readOnly ? 3 : 4} className="py-2" />
                  <TableCell className="py-2 text-right">
                    <span className="text-xs font-heading font-bold tabular-nums text-[#1f2933]">{fmtTotal(totalBudget)}</span>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <span className="text-xs font-heading font-bold tabular-nums text-[#1f2933]">{fmtTotal(totalListed)}</span>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <span className="text-xs font-heading font-bold tabular-nums text-[#1f2933]">{fmtTotal(totalOrdered)}</span>
                  </TableCell>
                  <TableCell colSpan={readOnly ? 2 : 4} className="py-2" />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
      <ReceiptViewerPanel
        open={!!viewingReceipt}
        onClose={() => { setViewingReceipt(null); setViewingItemId(null); }}
        receipt={viewingReceipt}
        title="Item receipt"
        extras={viewingReceipt && viewingItemId ? (() => {
          // Auto-committed receipts carry a line_item_summary keyed by item id.
          const sum = (viewingReceipt.line_item_summary || []).find(s => s.item_id === viewingItemId);
          if (!sum) {
            return viewingReceipt.amount != null ? (
              <p className="text-xs text-[#5b6773]">
                Receipt total: <span className="tabular-nums text-[#1f2933] font-semibold">${Math.round(Number(viewingReceipt.amount)).toLocaleString('en-US')}</span>
              </p>
            ) : null;
          }
          return (
            <div className="space-y-2 text-xs">
              <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold">Charged to this item</p>
              <div className="flex items-center justify-between">
                <span className="text-[#1f2933] font-medium">Total</span>
                <span className="tabular-nums font-semibold text-[#2d948c]">${Math.round(Number(sum.amount)).toLocaleString('en-US')}</span>
              </div>
              <div className="space-y-0.5 pt-1 border-t">
                <p className="text-[10px] uppercase tracking-wider text-[#5b6773]/80 pt-1">Line items</p>
                {(sum.lines || []).map((ln, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="truncate pr-2 text-[#5b6773]">{ln.line_item_name}{ln.count > 1 ? ` × ${ln.count}` : ''}</span>
                    <span className="tabular-nums text-[#1f2933]">${Math.round(Number(ln.cost || 0)).toLocaleString('en-US')}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })() : null}
      />
      <EssentialPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={async (name) => {
          await onAddItem({ name, is_essential: true });
        }}
      />
      <FillInWizard
        open={!!fillInKind}
        onClose={() => setFillInKind(null)}
        room={room}
        kind={fillInKind || 'items'}
        receiptsByItem={receiptsByItem}
        onUpdateItem={onUpdateItem}
        onUploadReceipt={onUploadReceipt}
      />
    </Card>
  );
};
